<ng-container *ngIf="profile as p; else missing">
  <header class="profile-head">
    <div class="avatar large">{{ p.name.charAt(0).toUpperCase() }}</div>
    <div class="profile-title">
      <h3>{{ p.name }}</h3>
      <small>{{ p.email || 'Keine E-Mail' }}</small>
      <span class="badges">
        <span class="badge" [class.muted]="!inPlanning" [matTooltip]="planningStatusText">
          <mat-icon *ngIf="inPlanning">event_available</mat-icon>{{ inPlanning ? 'In der Gesamtplanung' : 'Nicht in der Planung' }}
        </span>
        <span class="badge planning-team" *ngIf="plannedByTeam" matTooltip="Automatisch, weil Mitglied eines aktiven Teams">
          <mat-icon>groups</mat-icon>über Team
        </span>
        <span class="badge planning-manual" *ngIf="manuallyPlanned" matTooltip="Manuell aufgenommen – unabhängig von Teams">
          <mat-icon>person_pin</mat-icon>manuell
        </span>
        <span class="badge leader" *ngIf="leadsAnyTeam">
          <mat-icon>verified_user</mat-icon>Teamleitung
        </span>
        <span class="badge muted" *ngIf="p.hasCalendarFeed" matTooltip="Outlook-Kalender ist verknüpft">
          <mat-icon>event_available</mat-icon>Kalender
        </span>
      </span>
    </div>
    <button mat-icon-button (click)="closed.emit()" aria-label="Profil schließen"><mat-icon>close</mat-icon></button>
  </header>

  <!-- Teams & Hauptteam -->
  <section class="block">
    <h4 class="section-title">Teams <span class="count">{{ memberships.length }}</span></h4>

    <p class="hint warn" *ngIf="!memberships.length">
      <mat-icon>info_outline</mat-icon>
      Keinem Team zugeordnet – kein Hauptteam; Urlaubsanträge nur mit manuell festgelegtem Urlaubsgenehmiger.
      <ng-container *ngIf="manuallyPlanned"> In der Gesamtplanung durch manuelle Aufnahme.</ng-container>
    </p>

    <div class="team-rows" *ngIf="memberships.length">
      <div class="team-row" *ngFor="let m of memberships">
        <button type="button" class="team-link" (click)="openTeam.emit(m.teamId)" [matTooltip]="'Team ' + m.teamName + ' öffnen'">
          <mat-icon>groups</mat-icon>{{ m.teamName }}
        </button>
        <span class="badge leader" *ngIf="m.isLeader"><mat-icon>verified_user</mat-icon>Teamleitung</span>
        <span class="badge primary" *ngIf="m.teamId === p.primaryTeamId"><mat-icon>star</mat-icon>Hauptteam</span>
        <span class="spacer"></span>
        <button mat-icon-button *ngIf="canRemoveFrom(m)" [disabled]="store.busy" matTooltip="Aus diesem Team entfernen"
          (click)="removeFromTeam(m)" [attr.aria-label]="'Aus ' + m.teamName + ' entfernen'">
          <mat-icon>link_off</mat-icon>
        </button>
        <mat-icon class="locked" *ngIf="m.isLeader && store.canManageTeam(teamOf(m))"
          matTooltip="Die Teamleitung kann nicht entfernt werden – zuerst Teamleitung übertragen.">lock_outline</mat-icon>
      </div>
    </div>

    <!-- Hauptteam: nur bei mehreren Teams wählbar -->
    <p class="hint ok" *ngIf="memberships.length === 1">
      <mat-icon>star</mat-icon>
      Hauptteam: <strong>{{ memberships[0].teamName }}</strong> (automatisch, da nur ein Team)
    </p>
    <ng-container *ngIf="memberships.length > 1">
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="full" *ngIf="canChoosePrimary">
        <mat-label>Hauptteam (Urlaubsfreigabe)</mat-label>
        <mat-select [value]="p.primaryTeamId" (selectionChange)="setPrimary($event.value)" [disabled]="store.busy">
          <mat-option *ngFor="let m of memberships" [value]="m.teamId">{{ m.teamName }}</mat-option>
        </mat-select>
      </mat-form-field>
      <p class="hint" *ngIf="!canChoosePrimary && p.primaryTeamId">
        <mat-icon>star</mat-icon> Hauptteam: <strong>{{ primaryName }}</strong>
      </p>
      <p class="hint warn" *ngIf="!p.primaryTeamId">
        <mat-icon>warning_amber</mat-icon>
        Kein Hauptteam festgelegt – Urlaubsanträge sind erst nach der Auswahl möglich.
      </p>
    </ng-container>

    <div class="inline" *ngIf="addableTeams.length">
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Einem Team zuordnen</mat-label>
        <mat-select [(ngModel)]="addTeamId" [disabled]="store.busy">
          <mat-option *ngFor="let team of addableTeams" [value]="team.id">{{ team.name }}</mat-option>
        </mat-select>
      </mat-form-field>
      <button mat-stroked-button color="primary" [disabled]="addTeamId === null || store.busy" (click)="addToTeam()">
        <mat-icon>group_add</mat-icon> Zuordnen
      </button>
    </div>
  </section>

  <!-- Planung & Kapazität -->
  <section class="block">
    <h4 class="section-title">Planung & Kapazität</h4>
    <!-- Gesamtplanung: automatisch über Team, zusätzlich manuell möglich -->
    <ng-container *ngIf="canManagePlanning">
      <p class="hint ok" *ngIf="plannedByTeam && !manuallyPlanned">
        <mat-icon>groups</mat-icon>
        <span>Automatisch in der Gesamtplanung über {{ memberships.length === 1 ? 'das Team' : 'die Teams' }}
          <strong>{{ memberships.length === 1 ? memberships[0].teamName : memberships.length }}</strong>.
          Soll der Mitarbeiter auch ohne Team in der Gesamtplanung bleiben, kann er zusätzlich manuell aufgenommen werden.</span>
      </p>
      <p class="hint ok" *ngIf="manuallyPlanned">
        <mat-icon>person_pin</mat-icon>
        <span>Manuell in die Gesamtplanung aufgenommen – unabhängig von Teams.
          <ng-container *ngIf="plannedByTeam"> Zusätzlich automatisch über {{ memberships.length === 1 ? 'das Team' : 'die Teams' }}.</ng-container>
          <ng-container *ngIf="!plannedByTeam"> Erscheint in keiner Teamplanung, solange keine Teamzuordnung besteht.</ng-container></span>
      </p>
      <p class="hint" *ngIf="!inPlanning">
        <mat-icon>info_outline</mat-icon>
        Nicht in der Ressourcenplanung: erscheint automatisch, sobald er einem Team angehört – oder sofort nach manueller Aufnahme.
      </p>
      <div class="inline planning-actions">
        <button mat-stroked-button color="primary" *ngIf="!manuallyPlanned" [disabled]="store.busy" (click)="addToPlanning()">
          <mat-icon>person_add</mat-icon> {{ plannedByTeam ? 'Zusätzlich manuell aufnehmen' : 'Zur Gesamtplanung hinzufügen' }}
        </button>
        <button mat-stroked-button *ngIf="manuallyPlanned" [disabled]="store.busy" (click)="removeFromPlanning()">
          <mat-icon>person_remove</mat-icon> {{ plannedByTeam ? 'Manuelle Aufnahme aufheben' : 'Aus der Gesamtplanung entfernen' }}
        </button>
      </div>
    </ng-container>

    <ng-container *ngIf="canEditCore; else coreReadOnly">
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="hours">
        <mat-label>Wochenstunden</mat-label>
        <input matInput type="number" min="0.5" max="168" step="0.5" [(ngModel)]="weeklyHours" [disabled]="store.busy">
        <span matTextSuffix>h</span>
      </mat-form-field>
      <p class="hint">
        <mat-icon>donut_large</mat-icon>
        Entspricht {{ dailyHours | number:'1.0-2':'de-AT' }} h pro Werktag (Mo–Fr) = 100 % Auslastung.
      </p>
    </ng-container>
    <ng-template #coreReadOnly>
      <div class="facts">
        <div><small>Status</small><strong>{{ planningStatusText }}</strong></div>
        <div><small>Wochenstunden</small><strong>{{ p.weeklyCapacityHours | number:'1.0-2':'de-AT' }} h</strong></div>
        <div><small>Pro Werktag</small><strong>{{ p.weeklyCapacityHours / 5 | number:'1.0-2':'de-AT' }} h</strong></div>
      </div>
    </ng-template>
  </section>

  <!-- Skills -->
  <section class="block">
    <h4 class="section-title">Skills & Qualifikationen</h4>
    <ng-container *ngIf="canEditSkills">
      <mat-form-field appearance="outline" subscriptSizing="dynamic" class="full">
        <mat-label>Skills zuordnen</mat-label>
        <mat-select multiple [(ngModel)]="editableSkillIds" [disabled]="store.busy">
          <div class="select-search">
            <input class="select-search-input" placeholder="Skill suchen …" [(ngModel)]="skillSearch"
              (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">
          </div>
          <mat-option *ngFor="let skill of skillOptions" [value]="skill.id">
            {{ skill.name }}<span class="option-mail" *ngIf="skill.ownerTeamName"> · Team {{ skill.ownerTeamName }}</span>
          </mat-option>
        </mat-select>
      </mat-form-field>
      <div class="chips" *ngIf="selectedSkills.length">
        <span class="skill-chip" *ngFor="let skill of selectedSkills">
          <span class="skill-dot" [style.backgroundColor]="skill.color || '#607d8b'"></span>{{ skill.name }}
          <button type="button" class="chip-remove" (click)="removeSkill(skill.id)" [disabled]="store.busy"
            [attr.aria-label]="skill.name + ' entfernen'"><mat-icon>close</mat-icon></button>
        </span>
      </div>
    </ng-container>
    <div class="chips" *ngIf="fixedSkills.length">
      <span class="skill-chip" *ngFor="let skill of fixedSkills" [matTooltip]="canEditSkills ? 'Skill eines anderen Teams – hier nicht änderbar' : ''">
        <span class="skill-dot" [style.backgroundColor]="skill.color || '#607d8b'"></span>{{ skill.name }}
      </span>
    </div>
    <p class="empty-inline" *ngIf="!selectedSkills.length && !fixedSkills.length">Keine Skills zugeordnet.</p>
  </section>

  <footer class="profile-actions" *ngIf="canEditCore || canEditSkills">
    <button mat-button (click)="discard()" [disabled]="!dirty || store.busy">Verwerfen</button>
    <button mat-flat-button color="primary" (click)="save()" [disabled]="!dirty || store.busy">
      <mat-icon>save</mat-icon> Profil speichern
    </button>
  </footer>
</ng-container>

<ng-template #missing>
  <header class="profile-head">
    <div class="profile-title"><h3>Profil nicht verfügbar</h3></div>
    <button mat-icon-button (click)="closed.emit()" aria-label="Schließen"><mat-icon>close</mat-icon></button>
  </header>
  <p class="hint">Für dieses Profil fehlt dir die Berechtigung oder es existiert nicht mehr.</p>
</ng-template>
